import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';
import * as Sentry from '@sentry/browser';

import { queryClient } from '@hotjar/webapp/utils/react-query';
import { useCurrentSite } from '@hotjar/site';
import { getRecentSurveys } from '@hotjar/webapp/domains/surveys/api';
import type { RecentSurvey } from '@hotjar/webapp/domains/surveys/types';

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

export const RECENT_SURVEYS_QUERY_KEY = 'recentSurveys';

export const useRecentSurveys = (): UseQueryResult<RecentSurvey[], Error> => {
  const { data: currentSite } = useCurrentSite();
  const siteId = currentSite?.id;

  return useQuery(
    [RECENT_SURVEYS_QUERY_KEY, { siteId }],
    () => getRecentSurveys(siteId as number),
    {
      enabled: siteId !== undefined,
      retry: false,
      staleTime: CACHE_TIME,
      cacheTime: CACHE_TIME,
    },
  );
};

export const invalidateRecentSurveysCache = (siteId: number): void => {
  try {
    // Invalidate cache to make sure we show the new/updated survey on the dashboard overview
    queryClient.invalidateQueries([RECENT_SURVEYS_QUERY_KEY, siteId]);
  } catch {
    Sentry.captureMessage('Error invalidating recent surveys cache', Sentry.Severity.Info);
  }
};
